import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search, Download, Lock, Phone, CreditCard, User, MapPin, Briefcase, Shield, Loader2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import { partyInfo } from '../mockData';
import { MemberPerks } from '../components/MemberPerks';
import { RecruiterBadge } from '../components/join/RecruiterBadge';
import { nextBadge } from '../components/join/joinConfig';

const MemberCard = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [lookupValue, setLookupValue] = useState('');
  const [lookupType, setLookupType] = useState('mobile');
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const cardRef = useRef(null);

  const doLookup = async (type, value) => {
    if (!value.trim()) return;
    setLoading(true);
    setError('');
    setMember(null);
    try {
      const param = type === 'mobile' ? `mobile=${value.trim()}` : `nid=${value.trim()}`;
      const res = await fetch(`/api/membership/lookup?${param}`);
      if (res.ok) {
        setMember(await res.json());
      } else {
        setError(t({ en: 'Member not found. Please check your details.', bn: 'সদস্য পাওয়া যায়নি। অনুগ্রহ করে আপনার তথ্য পরীক্ষা করুন।' }));
      }
    } catch {
      setError(t({ en: 'Connection error. Please try again.', bn: 'সংযোগ ত্রুটি। অনুগ্রহ করে আবার চেষ্টা করুন।' }));
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup from URL params (redirect from registration)
  useEffect(() => {
    const mobile = searchParams.get('mobile');
    const nid = searchParams.get('nid');
    if (mobile) {
      setLookupValue(mobile);
      setLookupType('mobile');
      doLookup('mobile', mobile);
    } else if (nid) {
      setLookupValue(nid);
      setLookupType('nid');
      doLookup('nid', nid);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLookup = (e) => {
    e.preventDefault();
    doLookup(lookupType, lookupValue);
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: null, useCORS: true });
      const link = document.createElement('a');
      link.download = `NDP_Member_Card_${member.full_name.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const isApproved = member?.status === 'approved';
  const memberId = member?.member_number || (member ? `NDP-${member._id.slice(-8).toUpperCase()}` : '');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="member-card-page">
      {/* Hero — compact on mobile */}
      <section className="bg-gradient-to-br from-gray-900 via-party-red/90 to-gray-900 py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <CreditCard className="h-8 sm:h-12 w-8 sm:w-12 text-white mx-auto mb-3 opacity-80" />
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2">
            {t({ en: 'Member ID Card', bn: 'সদস্য পরিচয়পত্র' })}
          </h1>
          <p className="text-sm sm:text-lg text-red-200">
            {t({ en: 'View and download your digital ID card', bn: 'আপনার ডিজিটাল পরিচয়পত্র দেখুন ও ডাউনলোড করুন' })}
          </p>
        </div>
      </section>

      <div className="max-w-lg mx-auto px-4 py-6 sm:py-10">
        {/* Lookup Form */}
        <Card className="mb-6" data-testid="member-lookup-form">
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleLookup} className="space-y-3">
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={lookupType === 'mobile' ? 'default' : 'outline'} className={`flex-1 ${lookupType === 'mobile' ? 'bg-party-red hover:bg-red-700' : ''}`} onClick={() => setLookupType('mobile')} data-testid="lookup-type-mobile">
                  <Phone className="h-4 w-4 mr-1" /> {t({ en: 'Mobile', bn: 'মোবাইল' })}
                </Button>
                <Button type="button" size="sm" variant={lookupType === 'nid' ? 'default' : 'outline'} className={`flex-1 ${lookupType === 'nid' ? 'bg-party-red hover:bg-red-700' : ''}`} onClick={() => setLookupType('nid')} data-testid="lookup-type-nid">
                  <Shield className="h-4 w-4 mr-1" /> {t({ en: 'NID', bn: 'জাতীয় পরিচয়পত্র' })}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={lookupValue}
                  onChange={(e) => setLookupValue(e.target.value)}
                  placeholder={lookupType === 'mobile' ? t({ en: 'Enter mobile number', bn: 'মোবাইল নম্বর দিন' }) : t({ en: 'Enter NID number', bn: 'পরিচয়পত্র নম্বর দিন' })}
                  className="flex-1 text-base"
                  inputMode="numeric"
                  data-testid="lookup-input"
                />
                <Button type="submit" className="bg-party-green hover:bg-green-700 shrink-0" disabled={loading} data-testid="lookup-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              {error && <p className="text-red-500 text-sm" data-testid="lookup-error">{error}</p>}
            </form>
          </CardContent>
        </Card>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-10">
            <Loader2 className="h-8 w-8 text-party-red animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{t({ en: 'Loading your card...', bn: 'আপনার কার্ড লোড হচ্ছে...' })}</p>
          </div>
        )}

        {/* ID Card */}
        {member && !loading && (
          <div className="space-y-5">
            {/* Card Preview — responsive width */}
            <div className="flex justify-center">
              <div ref={cardRef} className="w-full max-w-[420px] rounded-2xl overflow-hidden shadow-2xl" data-testid="id-card-preview">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-party-red via-red-600 to-party-red p-3 sm:p-4 text-center relative">
                  <div className="absolute top-0 left-0 right-0 flex h-1">
                    <div className="flex-1 bg-party-green"></div>
                    <div className="flex-1 bg-white"></div>
                    <div className="flex-1 bg-party-red"></div>
                  </div>
                  <div className="flex items-center justify-center gap-2 sm:gap-3 pt-1">
                    <img src={partyInfo.flagImage} alt="NDP" className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-contain bg-white/20 p-1" crossOrigin="anonymous" />
                    <div>
                      <h2 className="text-white font-bold text-sm sm:text-base leading-tight">National Democratic Party</h2>
                      <p className="text-red-100 text-[9px] sm:text-[10px]">ন্যাশনাল ডেমোক্রেটিক পার্টি</p>
                    </div>
                  </div>
                  <div className="mt-1.5 bg-white/20 rounded-full px-3 py-0.5 inline-block">
                    <p className="text-white text-[10px] sm:text-xs font-bold tracking-wider">MEMBER ID CARD</p>
                  </div>
                </div>

                {/* Card Body */}
                <div className="bg-white p-3 sm:p-5">
                  <div className="flex gap-3">
                    <div className="w-16 h-20 sm:w-20 sm:h-24 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-2 border-party-red/20 shrink-0">
                      <User className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-tight truncate">{member.full_name}</h3>
                      <p className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-1"><CreditCard className="h-3 w-3 shrink-0" /> {memberId}</p>
                      {member.district && <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1"><MapPin className="h-3 w-3 text-party-green shrink-0" /> {member.district}</p>}
                      <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1"><Briefcase className="h-3 w-3 text-party-green shrink-0" /> {member.profession}</p>
                    </div>
                    {member.member_number && (
                      <div className="shrink-0 text-center" data-testid="id-card-qr">
                        <div className="rounded-lg border border-gray-200 bg-white p-1">
                          <QRCodeCanvas value={`${window.location.origin}/verify/${member.member_number}`} size={68} level="M" />
                        </div>
                        <p className="mt-0.5 text-[8px] text-gray-400">Scan to verify</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-1.5 sm:gap-2 text-[11px] sm:text-xs">
                    <div className="bg-gray-50 rounded-lg p-1.5 sm:p-2">
                      <p className="text-gray-400 text-[9px] sm:text-[10px]">Father/Mother</p>
                      <p className="font-medium text-gray-700 truncate">{member.father_name}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-1.5 sm:p-2">
                      <p className="text-gray-400 text-[9px] sm:text-[10px]">Education</p>
                      <p className="font-medium text-gray-700 truncate">{member.education}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-1.5 sm:p-2">
                      <p className="text-gray-400 text-[9px] sm:text-[10px]">Mobile</p>
                      <p className="font-medium text-gray-700">{member.mobile}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-1.5 sm:p-2">
                      <p className="text-gray-400 text-[9px] sm:text-[10px]">Member Since</p>
                      <p className="font-medium text-gray-700">{new Date(member.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    <RecruiterBadge badge={member.badge} size="md" testid="id-card-recruiter-badge" />
                    {isApproved ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold border border-green-200">
                        <Shield className="h-3 w-3" /> VERIFIED MEMBER
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-[11px] sm:text-xs font-bold border border-amber-200">
                        <Lock className="h-3 w-3" /> PENDING VERIFICATION
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-party-green via-emerald-600 to-party-green p-2 text-center">
                  <p className="text-white text-[9px] sm:text-[10px]">Show this card for member perks · www.ndpbd.com/join</p>
                </div>
              </div>
            </div>

            {/* Download — full width touch target on mobile */}
            <div className="space-y-3" data-testid="card-download-section">
              <Button onClick={handleDownload} disabled={downloading} className="w-full bg-party-green hover:bg-green-700 text-white py-3 text-base" data-testid="download-card-btn">
                {downloading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t({ en: 'Preparing...', bn: 'প্রস্তুত হচ্ছে...' })}</> : <><Download className="h-5 w-5 mr-2" /> {t({ en: 'Download ID Card', bn: 'পরিচয়পত্র ডাউনলোড করুন' })}</>}
              </Button>
              <div className="rounded-xl border bg-white p-3 text-sm text-gray-700" data-testid="card-referral-progress">
                {t({ en: 'Friends recruited', bn: 'যোগ করা বন্ধু' })}: <b>{member.referrals || 0}</b>
                {nextBadge(member.referrals || 0) && <span className="text-gray-500"> · {t({ en: `${nextBadge(member.referrals || 0)[1].min - (member.referrals || 0)} more for ${nextBadge(member.referrals || 0)[1].label}`, bn: `${nextBadge(member.referrals || 0)[1].bn} পেতে আরও ${nextBadge(member.referrals || 0)[1].min - (member.referrals || 0)} জন` })}</span>}
                {member.member_number && <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join?ref=${member.member_number}`).catch(() => {}); setInviteCopied(true); }} className="ml-1 font-semibold text-party-red underline" data-testid="card-invite-link">{inviteCopied ? t({ en: 'Invite link copied!', bn: 'আমন্ত্রণ লিংক কপি হয়েছে!' }) : t({ en: 'Copy my invite link', bn: 'আমার আমন্ত্রণ লিংক কপি করুন' })}</button>}
              </div>
              <MemberPerks mobile={member.mobile} />
              {!isApproved && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4" data-testid="pending-approval-notice">
                  <p className="text-xs sm:text-sm text-amber-700">
                    {t({
                      en: 'Your card is ready! To unlock full benefits, pay 100 BDT to 01732341999 (bKash/Nagad). Admin will verify your membership once payment is confirmed.',
                      bn: 'আপনার কার্ড প্রস্তুত! সম্পূর্ণ সুবিধা পেতে 01732341999 নম্বরে ১০০ টাকা পাঠান (bKash/নগদ)। পেমেন্ট নিশ্চিত হলে অ্যাডমিন আপনার সদস্যপদ যাচাই করবেন।'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!member && !error && !loading && (
          <div className="text-center py-10 text-gray-400">
            <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm sm:text-base">{t({ en: 'Enter your mobile number or NID to view your ID card', bn: 'আপনার পরিচয়পত্র দেখতে মোবাইল নম্বর দিন' })}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberCard;
